import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import JsonNode from './JsonNode';
import { HUD_JSON_TREE_ADD_ROW_HEIGHT, HUD_JSON_TREE_ROW_HEIGHT } from './constants';
import { formatPrimitive, getIn, getTopLevelEntrySizeBytes, isObjectLike, stringForMatch, subtreeHasMatch } from '../utils';

const listStyle = {
  height: '100%',
  width: '100%'
};

const sortLabels = ([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

const getObjectEntries = (value, pathKey, priorityMap) => {
  const sorted = Object.entries(value).sort(sortLabels);
  const priorityList = priorityMap[pathKey] || [];
  if (!priorityList.length) return sorted.map(([label, childValue]) => [label, childValue, label]);

  const prioritySet = new Set(priorityList);
  const prioritized = priorityList.map((label) => (Object.prototype.hasOwnProperty.call(value, label) ? [label, value[label], label] : null)).filter(Boolean);
  const remainder = sorted.filter(([label]) => !prioritySet.has(label)).map(([label, childValue]) => [label, childValue, label]);
  return [...prioritized, ...remainder];
};

const getChildEntries = (value, pathKey, priorityMap) => (Array.isArray(value) ? value.map((childValue, index) => [`[${index}]`, childValue, index]) : getObjectEntries(value, pathKey, priorityMap));

const matchesText = (value, normalizedSearch, exactMatchOnly) => {
  const lower = value.toLowerCase();
  return exactMatchOnly ? lower === normalizedSearch : lower.includes(normalizedSearch);
};

const primitiveMatches = (value, normalizedSearch, exactMatchOnly) =>
  matchesText(stringForMatch(value), normalizedSearch, exactMatchOnly) || matchesText(formatPrimitive(value), normalizedSearch, exactMatchOnly);

const buildRows = ({
  data,
  exactMatchOnly,
  highlightPathSet,
  openPathSet,
  priorityMap,
  searchTerm,
  showAllPropertiesOfMatchingNodes,
  sizePathKey,
  sortTopLevelBySize,
  visiblePathSet,
  visibleSubtreePathSet
}) => {
  const normalizedSearch = (searchTerm || '').trim().toLowerCase();
  const isFiltering = normalizedSearch.length > 0;
  const rows = [];

  const appendNode = ({ label, value, path, keyPath, level, showAllDescendants = false, showTopLevelSize = false }) => {
    const pathKey = keyPath.join('.');
    const visibleInChangedSubtree = [...(visibleSubtreePathSet || [])].some((root) => pathKey.startsWith(`${root}.`));
    if (visiblePathSet && !visiblePathSet.has(pathKey) && !visibleInChangedSubtree) return;
    const matchesLabel = isFiltering && matchesText(label, normalizedSearch, exactMatchOnly);
    const forceShowHere = showAllDescendants || matchesLabel;
    const highlighted = highlightPathSet?.has(pathKey);

    if (!isObjectLike(value)) {
      if (isFiltering && !forceShowHere && !primitiveMatches(value, normalizedSearch, exactMatchOnly)) return;
      rows.push({ label, value, path, keyPath, level, highlighted, showAllDescendants, showTopLevelSize });
      return;
    }

    if (!isFiltering && !openPathSet.has(path)) {
      rows.push({ label, value, path, keyPath, level, highlighted, showAllDescendants, showTopLevelSize });
      return;
    }

    const entries = getChildEntries(value, pathKey, priorityMap);
    const annotatedEntries = entries.map(([childLabel, childValue, childKey]) => ({
      childLabel,
      childValue,
      childKey,
      childMatchesFilter: isFiltering ? subtreeHasMatch(childLabel, childValue, normalizedSearch, exactMatchOnly) : false
    }));
    const hasMatchingDescendant = annotatedEntries.some(({ childMatchesFilter }) => childMatchesFilter);
    const shouldFilterChildren = showAllPropertiesOfMatchingNodes ? isFiltering && !(showAllDescendants || matchesLabel || hasMatchingDescendant) : isFiltering && !showAllDescendants;
    const visibleEntries = shouldFilterChildren ? annotatedEntries.filter(({ childMatchesFilter }) => childMatchesFilter) : annotatedEntries;

    if (!forceShowHere && isFiltering && !visibleEntries.length) return;

    rows.push({ label, value, path, keyPath, level, highlighted, showAllDescendants, showTopLevelSize });
    if (!openPathSet.has(path)) return;

    visibleEntries.forEach(({ childLabel, childValue, childKey, childMatchesFilter }) => {
      appendNode({
        label: childLabel,
        value: childValue,
        path: `${path}.${childLabel}`,
        keyPath: [...keyPath, childKey],
        level: level + 1,
        showAllDescendants: showAllPropertiesOfMatchingNodes ? true : childMatchesFilter,
        showTopLevelSize: pathKey === sizePathKey
      });
    });
  };

  if (!isObjectLike(data)) {
    appendNode({ label: 'map', value: data, path: 'map', keyPath: ['map'], level: 0 });
    return rows;
  }

  let topLevelEntries = getChildEntries(data, '', priorityMap);
  if (!Array.isArray(data) && sortTopLevelBySize) {
    topLevelEntries = topLevelEntries
      .map((entry) => ({ entry, size: getTopLevelEntrySizeBytes(entry[0], entry[1]) ?? -1 }))
      .sort((a, b) => {
        if (b.size !== a.size) return b.size - a.size;
        return sortLabels(a.entry, b.entry);
      })
      .map(({ entry }) => entry);
  }

  topLevelEntries.forEach(([label, value, key]) => {
    appendNode({
      label,
      value,
      path: Array.isArray(data) ? `map${label}` : `map.${label}`,
      keyPath: [key],
      level: 0,
      showTopLevelSize: !Array.isArray(data)
    });
  });

  return rows;
};

const HudJsonRow = ({ data, index, style }) => {
  const { changeLabelMap, nodeProps, rows, sizeData } = data;
  const row = rows[index];
  const sizeBytes = row.showTopLevelSize ? getTopLevelEntrySizeBytes(row.label, getIn(sizeData, row.keyPath)) : null;
  const sizeLabel = typeof sizeBytes === 'number' ? `${sizeBytes.toLocaleString()} B` : '';
  const changeLabel = changeLabelMap?.get(row.keyPath.join('.'));
  const labelSuffix = sizeLabel || changeLabel ? ` (${[sizeLabel, changeLabel].filter(Boolean).join(', ')})` : '';

  return (
    <div style={{ ...style, paddingRight: 8 }}>
      <JsonNode {...nodeProps} {...row} labelSuffix={labelSuffix} appearance="hud" defaultOpen={false} renderChildren={false} />
    </div>
  );
};

HudJsonRow.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired
};

const HudVirtualizedJsonTree = ({
  changeLabelMap,
  data,
  sizeData,
  searchTerm,
  highlightPathSet,
  onEdit,
  supportsTouch,
  onAddProperty,
  activeInlineAddPath,
  onInlineAddPathChange,
  priorityMap,
  exactMatchOnly,
  sizePathKey,
  sortTopLevelBySize,
  showAllPropertiesOfMatchingNodes,
  visiblePathSet,
  visibleSubtreePathSet
}) => {
  const listRef = useRef(null);
  const [openPathSet, setOpenPathSet] = useState(() => new Set());

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [activeInlineAddPath]);

  const handleOpenChange = useCallback((path, open) => {
    setOpenPathSet((current) => {
      const next = new Set(current);
      if (open) next.add(path);
      else {
        next.forEach((openPath) => {
          if (openPath === path || openPath.startsWith(`${path}.`)) next.delete(openPath);
        });
      }
      return next;
    });
  }, []);

  const rows = useMemo(
    () =>
      buildRows({
        data,
        exactMatchOnly,
        highlightPathSet,
        openPathSet,
        priorityMap,
        searchTerm,
        showAllPropertiesOfMatchingNodes,
        sizePathKey,
        sortTopLevelBySize,
        visiblePathSet,
        visibleSubtreePathSet
      }),
    [data, exactMatchOnly, highlightPathSet, openPathSet, priorityMap, searchTerm, showAllPropertiesOfMatchingNodes, sizePathKey, sortTopLevelBySize, visiblePathSet, visibleSubtreePathSet]
  );

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [rows]);

  const nodeProps = useMemo(
    () => ({
      searchTerm,
      onEdit,
      supportsTouch,
      onAddProperty,
      activeInlineAddPath,
      onInlineAddPathChange,
      priorityMap,
      exactMatchOnly,
      showAllPropertiesOfMatchingNodes,
      openPathSet,
      onOpenChange: handleOpenChange
    }),
    [activeInlineAddPath, exactMatchOnly, handleOpenChange, onAddProperty, onEdit, onInlineAddPathChange, openPathSet, priorityMap, searchTerm, showAllPropertiesOfMatchingNodes, supportsTouch]
  );

  if (data === undefined) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
        Locking initial status...
      </Typography>
    );
  }

  if (!data && data !== 0 && data !== false) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
        No CLT status available.
      </Typography>
    );
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.6)' }}>
        (empty)
      </Typography>
    );
  }

  return (
    <div style={listStyle}>
      <AutoSizer>
        {({ height, width }) => (
          <VariableSizeList
            ref={listRef}
            height={height}
            width={width}
            itemCount={rows.length}
            itemData={{ changeLabelMap, nodeProps, rows, sizeData: sizeData ?? data }}
            itemKey={(index) => rows[index].path}
            itemSize={(index) => (rows[index].keyPath.join('.') === activeInlineAddPath ? HUD_JSON_TREE_ADD_ROW_HEIGHT : HUD_JSON_TREE_ROW_HEIGHT)}
            overscanCount={10}>
            {HudJsonRow}
          </VariableSizeList>
        )}
      </AutoSizer>
    </div>
  );
};

HudVirtualizedJsonTree.propTypes = {
  changeLabelMap: PropTypes.instanceOf(Map),
  data: PropTypes.any,
  sizeData: PropTypes.any,
  searchTerm: PropTypes.string,
  highlightPathSet: PropTypes.instanceOf(Set),
  onEdit: PropTypes.func,
  supportsTouch: PropTypes.bool,
  onAddProperty: PropTypes.func,
  activeInlineAddPath: PropTypes.string,
  onInlineAddPathChange: PropTypes.func,
  priorityMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  exactMatchOnly: PropTypes.bool,
  sizePathKey: PropTypes.string,
  sortTopLevelBySize: PropTypes.bool,
  showAllPropertiesOfMatchingNodes: PropTypes.bool,
  visiblePathSet: PropTypes.instanceOf(Set),
  visibleSubtreePathSet: PropTypes.instanceOf(Set)
};

export default HudVirtualizedJsonTree;
